import React, { ReactNode, useEffect, useRef } from 'react';
import { useSecureConfigStore, useConfigStore } from './store/config/useConfigStore';
import { LoadingOverlay } from 'components/core/loading-overlay';
import { TriangleAlert } from 'lucide-react';
import { useSiteConfigurationSettings } from 'hooks/use-configuration-settings';
import { HttpError } from 'lib/https';
import { useAuthStore } from './store/auth/useAuthStore';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionStore } from './store/subscription';
import { useGetAccount } from 'features/profile/hooks/use-account';
import {
  getOnboardingLoginPath,
  isOnboardingLocation,
  markOnboardingShown,
  useOnboardingDoneElsewhere,
} from 'features/onboarding/onboarding-journey';
import { isOnboardingEnabled } from 'config/feature-flags';
import { useCommonStore } from './store/common/useCommonStore';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';
import posthog from 'analytics/posthogClient';
import { useCollaborationSocketStore } from './store/collaboration/use-collaboration-store';
import { getCurrentSubscription, mapCurrentSubscription } from 'features/subscription/services/plan-subscription-service';
import { useFreeAccess, useMyPlan } from 'features/subscription/hooks/use-seats';

const PAYMENT_RESULT_PATHS = ['/payment-success', '/payment/result'];

export const PostAuthMiddleware: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { logout, isAuthenticated } = useAuthStore();
  const { data: siteConfigurationsSettings, isLoading, error } = useSiteConfigurationSettings({
    enabled: isAuthenticated,
  });
  const { setSiteConfigurationSettings } = useSecureConfigStore();
  const { configurationSettings } = useConfigStore();
  const navigate = useNavigate();
  const { setCurrentPlan, setSubscriptionDetails, setLoading, setError, clearSubscription } = useSubscriptionStore();

  // Fetch user account data and update common store — never after logout.
  const { data: accountData, isLoading: accountLoading } = useGetAccount({
    enabled: isAuthenticated && !!configurationSettings,
  });
  const { setUser } = useCommonStore();
  const { hasPlan } = useMyPlan();
  const { isFreeAccess, isLoading: isFreeAccessLoading } = useFreeAccess();

  useEffect(() => {
    if (siteConfigurationsSettings) {
      setSiteConfigurationSettings(siteConfigurationsSettings);
    }
  }, [siteConfigurationsSettings, setSiteConfigurationSettings]);

  useEffect(() => {
    if (!isAuthenticated || !configurationSettings || isFreeAccessLoading) {
      return;
    }
    // Free access skips the subscription check. The store is persisted, so a plan kept from an
    // earlier login is cleared too, and every plan gate passes.
    if (isFreeAccess) {
      clearSubscription();
      return;
    }

    let isCancelled = false;
    getCurrentSubscription(configurationSettings)
      .then((res: unknown) => {
        if (isCancelled) return;
        const mapped = mapCurrentSubscription(res);
        if (mapped) {
          setSubscriptionDetails(mapped);
          setCurrentPlan(mapped);
        }
      })
      .catch((err) => {
        if (isCancelled) return;
        console.error('Failed to fetch current subscription details after login:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch subscription');
      })
      .finally(() => {
        if (!isCancelled) setLoading(false);
      });
    return () => {
      isCancelled = true;
    };
  }, [
    isAuthenticated,
    configurationSettings,
    isFreeAccess,
    isFreeAccessLoading,
    setCurrentPlan,
    setSubscriptionDetails,
    setError,
    setLoading,
    clearSubscription,
  ]);

  // Update common store with fresh user data when account data is available
  useEffect(() => {
    if (accountData && !accountLoading) {
      setUser(accountData);
      trackEvent(EVENTS.USER_LOGGED_IN(accountData.itemId));
      try {
        posthog.identify(accountData.itemId);
      } catch (error) {
        console.log('Failed to identify user:', error);
      }
    }
  }, [accountData, accountLoading, setUser]);

  // Every new login reopens unfinished onboarding at the step the user reached. A reload keeps
  // the same login count, so it doesn't. Skip the redirect when the login already lands inside
  // the journey so it keeps that step. Without a plan the chat page shows the no-plan card, and
  // onboarding waits for a login that has a plan. The payment result page keeps the user there:
  // the plan arrives while they read it, and onboarding opens once they leave it.
  // The shared preference is read first: it decides whether onboarding is done (also in the Klax
  // app) or needs the one-time catch-up, and it holds another device's progress to resume from.
  // Runs once per login: `navigate` changes on every route change, which would re-sync on every page.
  // VITE_ENABLE_ONBOARDING=false turns the whole journey off (no sync, no redirect).
  const syncedLoginRef = useRef<string | null>(null);
  // Onboarding finished on another tab or in the Klax app closes the local journey (C12).
  useOnboardingDoneElsewhere();
  useEffect(() => {
    if (!isOnboardingEnabled || !accountData || !hasPlan) return;
    if (PAYMENT_RESULT_PATHS.includes(window.location.pathname)) return;
    const { itemId, logInCount } = accountData;
    const loginKey = `${itemId}:${logInCount}`;
    if (syncedLoginRef.current === loginKey) return;
    syncedLoginRef.current = loginKey;

    // Onboarding is optional. If the preference can't be read, this resolves to null and nothing is
    // redirected or saved: the user may have finished it already.
    getOnboardingLoginPath(itemId, logInCount).then((resumePath) => {
      if (!resumePath) return;

      markOnboardingShown(itemId, logInCount);
      if (!isOnboardingLocation(window.location)) {
        navigate(resumePath, { replace: true });
      }
    });
  }, [accountData, hasPlan, navigate]);

  // Initialize collaboration websocket after login and configuration is ready
  const init = useCollaborationSocketStore((state) => state.init);
  const reconnectWithFreshToken = useCollaborationSocketStore((state) => state.reconnectWithFreshToken);
  const connectionStatus = useCollaborationSocketStore((state) => state.connectionStatus);
  const storedConfig = useCollaborationSocketStore((state) => state.configurationSettings);
  const accessToken = useAuthStore((s) => s.accessToken);

  // Use ref to track if we've already initialized to prevent re-initialization on navigation
  const hasInitializedRef = React.useRef(false);
  const prevAccessTokenRef = React.useRef<string | null>(null);

  useEffect(() => {

    if (
      configurationSettings &&
      accountData &&
      !accountLoading &&
      !hasInitializedRef.current &&
      (connectionStatus === 'DISCONNECTED' || storedConfig?.blocksKey !== configurationSettings.blocksKey)
    ) {

      init(configurationSettings);
      hasInitializedRef.current = true;
    }

    if (!accountData && hasInitializedRef.current) {
      hasInitializedRef.current = false;
    }

  }, [
    configurationSettings?.blocksKey,
    accountData?.itemId,
    accountLoading,
    connectionStatus,
    storedConfig?.blocksKey
  ]);

  // Same pattern as HTTP token refresh — reconnect WS when JWT rotates while already connected
  useEffect(() => {
    const prevToken = prevAccessTokenRef.current;
    prevAccessTokenRef.current = accessToken ?? null;

    const shouldReconnect =
      configurationSettings &&
      accountData &&
      !accountLoading &&
      hasInitializedRef.current &&
      prevToken !== null &&
      accessToken !== null &&
      prevToken !== accessToken;

    if (shouldReconnect) {
      reconnectWithFreshToken();
    }
  }, [
    accessToken,
    accountData,
    accountLoading,
    configurationSettings,
    reconnectWithFreshToken,
  ]);

  // After long idle (tab backgrounded / proxy timeout), reopen the socket when the user returns.
  useEffect(() => {
    if (!configurationSettings || !hasInitializedRef.current) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void useCollaborationSocketStore.getState().ensureConnected();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [configurationSettings?.blocksKey]);

  if (!isAuthenticated) {
    return null;
  }

  if (isLoading || accountLoading) {
    return <LoadingOverlay />;
  }

  if (error || !siteConfigurationsSettings) {
    const errorMessage = error?.message ?? 'Failed to load configuration settings';
    const parseError = error instanceof HttpError ? error.error : { error: errorMessage };
    if (parseError.error === 'invalid_refresh_token') {
      logout();
      navigate('/login');
    }
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div className="rounded-lg bg-red-50 border border-red-400 p-4 mb-2 transition-all duration-1000 ease-in-out opacity-100">
          <div className="flex items-center gap-1">
            <TriangleAlert className="text-error w-4 h-4" />
            <h1 className="text-error text-sm font-semibold">Something went wrong</h1>
          </div>
          <p className="text-error text-xs font-normal">{errorMessage}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};


